import React from 'react';
import Flex from '../Flex/Flex';
import Checklist from '../Checklist/Checklist';
import Divider from '../Divider/Divider';
import BillingInfo from './BillingInfo';
import { LabeledSwitch } from '../forms/LabeledSwitch/LabeledSwitch';
import styles from './ResubscribeModal.module.css';

interface ResubscribeTabProps {
  planName: string;
  planDisplayName?: string;
  price: string;
  billingPeriod: 'monthly' | 'annual';
  billingPeriodText: string;
  features: Array<{ label: string; checked: boolean }>;
  selectedBilling: 'monthly' | 'annual';
  onBillingChange: (billing: 'monthly' | 'annual') => void;
  billingInfo?: {
    lastFour: string;
    cardType: string;
    name?: string;
    expirationMonth?: string;
    expirationYear?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    startedOn?: string;
  };
  onEditBilling?: () => void;
  showBillingToggle?: boolean;
}

export const ResubscribeTab: React.FC<ResubscribeTabProps> = ({
  planName,
  planDisplayName,
  price,
  billingPeriod,
  billingPeriodText,
  features,
  selectedBilling,
  onBillingChange,
  billingInfo,
  onEditBilling,
  showBillingToggle = true
}) => {
  return (
    <Flex direction="column" gap={6} style={{ width: '100%' }}>
      <Flex direction="column" gap={4}>
        {/* Billing Toggle */}
        {showBillingToggle && (
          <Flex justify="center" style={{ marginTop: '16px', width: '100%' }}>
            <LabeledSwitch
              options={['Monthly', 'Annual'] as const}
              selectedOption={selectedBilling === 'monthly' ? 'Monthly' : 'Annual'}
              setSelectedOption={(option) => onBillingChange(option === 'Monthly' ? 'monthly' : 'annual')}
              style={{ width: '100%' }}
            />
          </Flex>
        )}

        {/* Plan Details */}
        <Flex direction="column" gap={4}>
          <Flex gap={2} justify="space-between">
            <Flex direction="column" gap={1}>
              <div className="strong">
                {planDisplayName || planName}
              </div>
              <div className="weak">Billed {billingPeriodText}</div>
            </Flex>
            <Flex direction="column" gap={1}>
              <div className="strong">
                ${price}
              </div>
              <div className="weak">
                x {billingPeriod === 'monthly' ? '1 Month' : '12 Months'}
              </div>
            </Flex>
          </Flex>

          <Checklist
            color="primary"
            items={features}
          />
        </Flex>
      </Flex>

      {/* Billing Summary */}
      <Flex direction="column" className={styles.billingSummary}>
        <Flex justify="space-between" style={{ padding: '16px' }}>
          <div>1 x {planDisplayName || planName}</div>
          <div>${price}</div>
        </Flex>
        <Divider margin='0px'/>
        <Flex justify="space-between" style={{ padding: '16px', background: 'var(--sentiment-background-info)' }}>
          <div className="strong">Current Total:</div>
          <div className="strong">${price}</div>
        </Flex>
      </Flex>

      {/* Billing Information */}
      <BillingInfo
        cardType={billingInfo?.cardType}
        lastFour={billingInfo?.lastFour}
        name={billingInfo?.name}
        expirationMonth={billingInfo?.expirationMonth}
        expirationYear={billingInfo?.expirationYear}
        address={billingInfo?.address}
        city={billingInfo?.city}
        state={billingInfo?.state}
        zipCode={billingInfo?.zipCode}
        country={billingInfo?.country}
        startedOn={billingInfo?.startedOn}
        onEdit={onEditBilling}
      />
    </Flex>
  );
};

export default ResubscribeTab;